import { useMemo } from "react";
import { useParams } from "react-router-dom";
import useMediaWithLocation from "../hooks/useMediaWithLocation";
import { useGalleryFilters } from "../hooks/useGalleryFilters";
import { useGalleryModal } from "../hooks/useGalleryModal";
import GalleryGrid from "./GalleryGrid";
import GalleryModal from "./GalleryModal";
import Loading from "../../uiStatus/components/Loading";
import { formFieldClass, formShell } from "../../../styles/formShell";
import { galleryShell } from "../../../styles/galleryShell";
import { typeShell } from "../../../styles/typeShell";

function GalleryContent() {
    const { projectId } = useParams();
    const { mediaWithLocation, loading, error, deleteMedia } = useMediaWithLocation(projectId);
    const {
        searchKeyword,
        setSearchKeyword,
        mediaTypeFilter,
        setMediaTypeFilter,
        sortField,
        setSortField,
        sortMode,
        setSortMode,
        filterAndSortMedia,
        getSearchStats
    } = useGalleryFilters();
    const { selectedMedia, selectedLocation, openModal, closeModal } = useGalleryModal();

    const sortedMedia = useMemo(
        () => filterAndSortMedia(mediaWithLocation),
        [filterAndSortMedia, mediaWithLocation]
    );

    const searchStats = useMemo(
        () => getSearchStats(mediaWithLocation),
        [getSearchStats, mediaWithLocation]
    );

    return (
        <div className={galleryShell.root}>
            <div className={galleryShell.controls}>
                <div className={galleryShell.searchSection}>
                    <label htmlFor="gallery-search-input" className={formShell.label}>
                        <span className={galleryShell.icon} aria-hidden="true">search</span>
                        検索
                    </label>
                    <input
                        id="gallery-search-input"
                        type="text"
                        placeholder="キーワードを入力..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className={formFieldClass(false)}
                    />
                </div>

                <div className={galleryShell.section}>
                    <label htmlFor="gallery-filter-select" className={formShell.label}>
                        <span className={galleryShell.icon} aria-hidden="true">filter_list</span>
                        フィルター
                    </label>
                    <select
                        id="gallery-filter-select"
                        value={mediaTypeFilter}
                        onChange={(e) => setMediaTypeFilter(e.target.value)}
                        className={formFieldClass(false, 'select')}
                    >
                        <option value="all">すべて</option>
                        <option value="image">画像</option>
                        <option value="video">動画</option>
                        <option value="audio">音声</option>
                    </select>
                </div>

                <div className={galleryShell.section}>
                    <label htmlFor="gallery-sort-select" className={formShell.label}>
                        <span className={galleryShell.icon} aria-hidden="true">sort</span>
                        ソート
                    </label>
                    <select
                        id="gallery-sort-select"
                        value={sortField === "none" ? "none" : `${sortField}-${sortMode}`}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "none") {
                                setSortField("none");
                            } else {
                                const [field, mode] = value.split("-");
                                setSortField(field);
                                setSortMode(mode);
                            }
                        }}
                        className={formFieldClass(false, 'select')}
                    >
                        <option value="none">なし</option>
                        <option value="title-asc">タイトル（昇順）</option>
                        <option value="title-desc">タイトル（降順）</option>
                        <option value="date-asc">日付（昇順）</option>
                        <option value="date-desc">日付（降順）</option>
                    </select>
                </div>
            </div>

            <div className={galleryShell.scrollable}>
                {loading ? (
                    <Loading isLoading />
                ) : error ? (
                    <p className={`p-3 text-center text-error ${typeShell.body}`}>
                        データの読み込みに失敗しました。
                    </p>
                ) : (
                    <GalleryGrid
                        items={sortedMedia}
                        onItemClick={openModal}
                        emptyMessage={
                            searchStats.hasSearch
                                ? "条件に一致するメディアがありません"
                                : "メディアがありません"
                        }
                    />
                )}
            </div>

            {selectedMedia && selectedLocation && (
                <GalleryModal
                    media={selectedMedia}
                    location={selectedLocation}
                    onClose={closeModal}
                    onDelete={deleteMedia}
                />
            )}
        </div>
    );
}

export default GalleryContent;
